import { nanoid } from "nanoid";

import { subscribableToAsyncGenerator } from "../utils/subscribable-to-async-generator";
import { type QueryStateEntry, deriveQueryResult } from "./query-state.atom";
import type { QueryError, QueryResponse, QueryResult } from "./request-page.types";

// =============================================================================
// Query Executor Input Type
// =============================================================================

export type QueryExecutorInput = {
	pageName: string;
	key: string;
	params: Record<string, string>;
	/** True when the key was explicitly provided by the caller (not auto-generated). */
	isStableKey?: boolean;
	/** When provided, skip the title-based page lookup and use this page ID directly. */
	pageId?: string;
};

// =============================================================================
// Query Handle Types
// =============================================================================

export type QueryHandle = {
	readonly key: string;
	readonly pageName: string;
	readonly params: Record<string, string>;

	/** Execute query and return response. Throws on error. */
	fetch(options?: { signal?: AbortSignal }): Promise<QueryResponse>;

	/** Infinite async iterator. Yields current state, then waits for refetch() calls. */
	stream(options?: { signal?: AbortSignal }): AsyncIterable<QueryResult>;

	/** Trigger a refetch. Pushes new result to stream() subscribers. */
	refetch(): Promise<QueryResult>;

	/** Abort any in-flight request for this query. */
	abort(): void;
};

export type QueryBuilderOptions = {
	/** The request page name to execute */
	request: string;
	/** Unique key for caching/deduplication. Auto-generated if not provided. */
	key?: string;
	/** Variables for `{{vars.X}}` substitution in the request. */
	vars?: Record<string, string>;
	/** Alias for `vars`. Both are merged; `vars` wins on key conflicts. */
	params?: Record<string, string>;
};

export type QueryBuilder = (options: QueryBuilderOptions) => QueryHandle;

/**
 * Subscribe to state changes for a query key.
 * Returns an unsubscribe function.
 */
export type SubscribeToState = (
	key: string,
	onChange: (state: QueryStateEntry) => void
) => () => void;

/**
 * Creates a simple query error.
 */
function createError(message: string): QueryError {
	return { code: "UNKNOWN", message };
}

// =============================================================================
// Query Handle Factory
// =============================================================================

/**
 * Creates a QueryHandle for a specific query.
 *
 * @param dispatch - The bound dispatch function from useSetAtom($queryExecutor(documentId))
 * @param getState - Function to get current query state
 * @param subscribeToState - Function to subscribe to state changes
 * @param pageName - The request page name to execute
 * @param key - Unique key for this query's state
 * @param params - Parameters for variable substitution
 */
export function createQueryHandle(
	dispatch: (input: QueryExecutorInput) => Promise<QueryStateEntry>,
	getState: (key: string) => QueryStateEntry,
	subscribeToState: SubscribeToState,
	pageName: string,
	key: string,
	params: Record<string, string>,
	isStableKey?: boolean
): QueryHandle {
	const refetch = async (): Promise<QueryResult> => {
		const state = await dispatch({ pageName, key, params, isStableKey });
		return deriveQueryResult(state, refetch);
	};

	const handle: QueryHandle = {
		key,
		pageName,
		params,

		async fetch(options?: { signal?: AbortSignal }): Promise<QueryResponse> {
			if (options?.signal?.aborted) {
				throw createError("Query was aborted");
			}

			const onAbort = () => handle.abort();
			options?.signal?.addEventListener("abort", onAbort, { once: true });

			try {
				const state = await dispatch({ pageName, key, params, isStableKey });
				const result = deriveQueryResult(state, refetch);

				if (result.error) {
					throw result.error;
				}

				if (!result.data) {
					throw createError("No data returned from query");
				}

				return result.data;
			} finally {
				options?.signal?.removeEventListener("abort", onAbort);
			}
		},

		async *stream(options?: { signal?: AbortSignal }): AsyncIterable<QueryResult> {
			if (options?.signal?.aborted) {
				return;
			}

			// Yield current cached state if we have data
			const currentState = getState(key);
			if (currentState.data !== undefined) {
				yield deriveQueryResult(currentState, refetch);
			}

			// Execute initial fetch
			const initialState = await dispatch({ pageName, key, params, isStableKey });
			yield deriveQueryResult(initialState, refetch);

			// Subscribe to state changes and convert to async iterator
			const stateChanges = subscribableToAsyncGenerator<QueryStateEntry>(
				(onValue) =>
					subscribeToState(key, (state) => {
						// Only emit when fetch completes (idle after fetching)
						if (state.fetchStatus === "idle") {
							onValue(state);
						}
					}),
				options?.signal
			);

			for await (const state of stateChanges) {
				yield deriveQueryResult(state, refetch);
			}
		},

		refetch,

		abort(): void {
			const state = getState(key);
			if (state.abortController) {
				try {
					state.abortController.abort();
				} catch {
					// Ignore abort errors
				}
			}
		},
	};

	return handle;
}

/**
 * Creates a QueryBuilder function for use in code cells.
 *
 * @param dispatch - Bound dispatch from useSetAtom($queryExecutor(documentId))
 * @param getState - Function to get current query state
 * @param subscribeToState - Function to subscribe to state changes
 */
export function createQueryBuilder(
	dispatch: (input: QueryExecutorInput) => Promise<QueryStateEntry>,
	getState: (key: string) => QueryStateEntry,
	subscribeToState: SubscribeToState
): QueryBuilder {
	return (options: QueryBuilderOptions) => {
		const isStableKey = options.key !== undefined;
		const { request: pageName, key = nanoid() } = options;
		// Merge params and vars — vars takes precedence on key conflicts.
		// Both are supported for backward compatibility.
		const mergedParams = { ...(options.params ?? {}), ...(options.vars ?? {}) };
		return createQueryHandle(
			dispatch,
			getState,
			subscribeToState,
			pageName,
			key,
			mergedParams,
			isStableKey
		);
	};
}
